import React, { useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Download } from 'lucide-react';
import {
    importProspectsPreview, importProspectsCommit,
    importProjectsPreview, importProjectsCommit,
    API_URL,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const ACTION_LABELS = {
    create: 'Créer',
    update: 'Mettre à jour',
    skip: 'Ignorer',
};

const actionBadgeClass = (a) => {
    if (a === 'create') return 'bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/30';
    if (a === 'update') return 'bg-[#0284C7]/10 text-[#0284C7] border-[#0284C7]/30';
    return 'bg-[#71717A]/10 text-[#52525B] border-[#71717A]/30';
};

const ImportPanel = ({ kind, previewFn, commitFn, sampleExportUrl, sampleExportLabel, labelColumns, supportsChildren = false }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null); // { rows, counts, child_summary?, child_rows? }
    const [loading, setLoading] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [includeChildren, setIncludeChildren] = useState({ factures: false, couts: false, notes: false });

    const reset = () => {
        setFile(null);
        setPreview(null);
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        setPreview(null);
    };

    const handlePreview = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const res = await previewFn(file);
            setPreview(res.data);
            toast.success(`Aperçu généré : ${res.data.counts.total} ligne(s) analysée(s)`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur d\'analyse du fichier');
        } finally {
            setLoading(false);
        }
    };

    const setRowAction = (idx, action) => {
        setPreview((prev) => {
            if (!prev) return prev;
            const rows = prev.rows.map((r, i) => (i === idx ? { ...r, action } : r));
            return { ...prev, rows };
        });
    };

    const setBulkAction = (action) => {
        setPreview((prev) => {
            if (!prev) return prev;
            const rows = prev.rows.map((r) => ({ ...r, action }));
            return { ...prev, rows };
        });
    };

    const handleCommit = async () => {
        if (!preview) return;
        const rowsToSend = preview.rows.map((r) => ({
            row_index: r.row_index,
            raw: r.raw,
            match_id: r.match_id,
            prospect_match_id: r.prospect_match_id,
            action: r.action || r.suggested_action,
        }));
        const payload = { rows: rowsToSend, batch_id: preview.batch_id };
        if (supportsChildren && (includeChildren.factures || includeChildren.couts || includeChildren.notes)) {
            payload.include_children = includeChildren;
            payload.child_rows = preview.child_rows || {};
        }
        setCommitting(true);
        try {
            const res = await commitFn(payload);
            const { created, updated, skipped, child_counts, errors } = res.data;
            let msg = `Import terminé : ${created} créé(s), ${updated} mis à jour, ${skipped} ignoré(s)`;
            if (child_counts) {
                const cc = Object.entries(child_counts).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ');
                if (cc) msg += ` — ${cc}`;
            }
            toast.success(msg);
            if (errors?.length) {
                toast.error(`${errors.length} erreur(s) — voir la console`);
                console.warn('Import errors', errors);
            }
            reset();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setCommitting(false);
        }
    };

    const rows = preview?.rows || [];
    const counts = preview?.counts || {};
    const finalCounts = rows.reduce(
        (acc, r) => {
            const a = r.action || r.suggested_action;
            acc[a] = (acc[a] || 0) + 1;
            return acc;
        },
        { create: 0, update: 0, skip: 0 }
    );

    return (
        <div className="space-y-4">
            {!preview && (
                <Card className="p-6 space-y-4 rounded-sm" data-testid={`import-panel-${kind}`}>
                    <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-6 h-6 text-[#E85A24]" />
                        <div>
                            <h3 className="text-lg font-semibold text-[#0A0A0A]">Fichier Excel à importer</h3>
                            <p className="text-sm text-[#52525B]">Format attendu : fichier XLSX identique à celui produit par l&apos;export du CRM (max 10 Mo).</p>
                        </div>
                    </div>

                    <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 text-xs text-[#52525B]">
                        <div className="font-semibold text-[#0A0A0A] mb-1">Colonnes reconnues :</div>
                        <div>{labelColumns}</div>
                        {sampleExportUrl && (
                            <a
                                href={`${API_URL}${sampleExportUrl}`}
                                className="inline-flex items-center gap-1 text-[#E85A24] hover:underline mt-2"
                                data-testid={`import-export-link-${kind}`}
                            >
                                <Download className="w-3 h-3" /> Télécharger un {sampleExportLabel} pour référence
                            </a>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <input
                            type="file"
                            accept=".xlsx"
                            onChange={handleFile}
                            className="flex-1 h-10 border border-[#E4E4E7] rounded-sm px-3 text-sm bg-white"
                            data-testid={`import-file-${kind}`}
                        />
                        <Button
                            onClick={handlePreview}
                            disabled={!file || loading}
                            className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white h-10 rounded-sm"
                            data-testid={`import-preview-btn-${kind}`}
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                            Analyser
                        </Button>
                    </div>
                </Card>
            )}

            {preview && (
                <Card className="p-6 space-y-4 rounded-sm" data-testid={`import-preview-${kind}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <h3 className="text-lg font-semibold text-[#0A0A0A] flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-[#16A34A]" />
                                Aperçu — {counts.total} ligne(s)
                            </h3>
                            <p className="text-sm text-[#52525B]">
                                Suggestion initiale : <span className="font-semibold text-[#16A34A]">{counts.to_create} à créer</span>,{' '}
                                <span className="font-semibold text-[#0284C7]">{counts.to_update} à mettre à jour</span>
                                {counts.orphan_client ? <> · <span className="text-[#B45309] font-semibold">{counts.orphan_client} client(s) non retrouvé(s)</span></> : null}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setBulkAction('create')} className="rounded-sm text-xs" data-testid={`import-bulk-create-${kind}`}>Tout créer</Button>
                            <Button variant="outline" size="sm" onClick={() => setBulkAction('update')} className="rounded-sm text-xs" data-testid={`import-bulk-update-${kind}`}>Tout MAJ</Button>
                            <Button variant="outline" size="sm" onClick={() => setBulkAction('skip')} className="rounded-sm text-xs" data-testid={`import-bulk-skip-${kind}`}>Tout ignorer</Button>
                            <Button variant="outline" size="sm" onClick={reset} className="rounded-sm text-xs" data-testid={`import-reset-${kind}`}>Recommencer</Button>
                        </div>
                    </div>

                    <div className="border border-[#E4E4E7] rounded-sm overflow-hidden">
                        <div className="max-h-[500px] overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[#F4F4F5] sticky top-0 z-10">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-semibold text-[#52525B]">#</th>
                                        <th className="px-3 py-2 text-left font-semibold text-[#52525B]">Contenu</th>
                                        <th className="px-3 py-2 text-left font-semibold text-[#52525B]">Correspondance</th>
                                        <th className="px-3 py-2 text-left font-semibold text-[#52525B] w-40">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#E4E4E7]">
                                    {rows.map((r, i) => {
                                        const currentAction = r.action || r.suggested_action;
                                        return (
                                            <tr key={i} className="hover:bg-[#FAFAFA]" data-testid={`import-row-${kind}-${i}`}>
                                                <td className="px-3 py-2 text-xs text-[#71717A] align-top">{i + 1}</td>
                                                <td className="px-3 py-2 align-top">
                                                    <div className="text-xs text-[#0A0A0A] font-medium">
                                                        {r.raw.prenom || r.raw.Titre || r.raw.nom || r.raw.societe || `Ligne ${i + 1}`} {r.raw.nom && r.raw.prenom ? r.raw.nom : ''}
                                                    </div>
                                                    <div className="text-[11px] text-[#71717A] truncate max-w-md">
                                                        {r.raw.email || r.raw['N° devis'] || r.raw.telephone || r.raw.Client || ''}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 align-top">
                                                    {r.match_id ? (
                                                        <div className="text-xs">
                                                            <span className="text-[#16A34A]">Trouvé</span>
                                                            <div className="text-[11px] text-[#71717A]">{r.match_label}</div>
                                                            {r.prospect_match_label && (
                                                                <div className="text-[11px] text-[#0284C7] mt-1">
                                                                    Client : {r.prospect_match_label}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : r.prospect_match_id ? (
                                                        <div className="text-xs">
                                                            <span className="text-[#0284C7]">Client lié</span>
                                                            <div className="text-[11px] text-[#71717A]">{r.prospect_match_label}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-[#71717A]">Aucune</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 align-top">
                                                    <Select value={currentAction} onValueChange={(v) => setRowAction(i, v)}>
                                                        <SelectTrigger className={`h-8 text-xs rounded-sm border ${actionBadgeClass(currentAction)}`} data-testid={`import-action-${kind}-${i}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="create" data-testid={`import-action-create-${kind}-${i}`}>Créer</SelectItem>
                                                            {r.match_id && <SelectItem value="update" data-testid={`import-action-update-${kind}-${i}`}>Mettre à jour</SelectItem>}
                                                            <SelectItem value="skip" data-testid={`import-action-skip-${kind}-${i}`}>Ignorer</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {supportsChildren && preview.child_summary && (
                        <div className="border border-[#E4E4E7] rounded-sm p-4 bg-[#FAFAFA] space-y-2" data-testid={`import-children-${kind}`}>
                            <div className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-2">
                                <FileSpreadsheet className="w-4 h-4 text-[#0284C7]" />
                                Feuilles enfants détectées
                            </div>
                            <p className="text-[11px] text-[#52525B]">Les enfants seront rattachés aux dossiers créés/mis à jour ci-dessus. Cochez pour inclure.</p>
                            <div className="flex flex-wrap gap-3">
                                {[
                                    { key: 'factures', label: 'Factures' },
                                    { key: 'couts', label: 'Coûts' },
                                    { key: 'notes', label: 'Notes' },
                                ].map(({ key, label }) => {
                                    const count = preview.child_summary[key] || 0;
                                    const disabled = count === 0;
                                    return (
                                        <label
                                            key={key}
                                            className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm border ${
                                                disabled ? 'opacity-40 border-[#E4E4E7]' : 'cursor-pointer border-[#E4E4E7] hover:bg-white'
                                            } ${includeChildren[key] ? 'border-[#E85A24] bg-[#E85A24]/5' : ''}`}
                                            data-testid={`import-child-${kind}-${key}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={!!includeChildren[key]}
                                                disabled={disabled}
                                                onChange={(e) =>
                                                    setIncludeChildren((s) => ({ ...s, [key]: e.target.checked }))
                                                }
                                                className="accent-[#E85A24]"
                                            />
                                            <span className="font-semibold text-[#0A0A0A]">{label}</span>
                                            <span className="text-[#71717A]">({count} ligne{count > 1 ? 's' : ''})</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between border-t border-[#E4E4E7] pt-4 flex-wrap gap-3">
                        <div className="text-sm text-[#52525B]">
                            À exécuter :{' '}
                            <span className="font-semibold text-[#16A34A]">{finalCounts.create || 0} créer</span> ·{' '}
                            <span className="font-semibold text-[#0284C7]">{finalCounts.update || 0} MAJ</span> ·{' '}
                            <span className="font-semibold text-[#71717A]">{finalCounts.skip || 0} ignorer</span>
                        </div>
                        <Button
                            onClick={handleCommit}
                            disabled={committing}
                            className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white h-10 rounded-sm"
                            data-testid={`import-commit-btn-${kind}`}
                        >
                            {committing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            Confirmer l&apos;import
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default function ImportPage() {
    return (
        <div className="max-w-6xl mx-auto p-4 lg:p-6 space-y-6" data-testid="import-page">
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-[#0A0A0A] tracking-tight flex items-center gap-3">
                    <Upload className="w-7 h-7 text-[#E85A24]" />
                    Import Excel
                </h1>
                <p className="text-sm text-[#52525B] mt-1">
                    Réimportez des clients ou des chantiers depuis un fichier Excel exporté du CRM. Un aperçu vous permet de valider ligne par ligne avant d&apos;appliquer les changements.
                </p>
            </div>

            <div className="bg-[#B45309]/5 border border-[#B45309]/30 rounded-sm p-3 text-xs text-[#B45309] flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                    <strong>Réservé aux administrateurs.</strong> Les lignes existantes sont détectées automatiquement (email/téléphone/SIRET pour les clients ; ID ou n° de devis pour les projets). Toutes les actions sont tracées dans l&apos;audit log.
                </div>
            </div>

            <Tabs defaultValue="prospects">
                <TabsList className="bg-[#F4F4F5] rounded-sm">
                    <TabsTrigger value="prospects" data-testid="tab-import-prospects">Clients / Prospects</TabsTrigger>
                    <TabsTrigger value="projects" data-testid="tab-import-projects">Projets / Chantiers</TabsTrigger>
                </TabsList>

                <TabsContent value="prospects" className="mt-4">
                    <ImportPanel
                        kind="prospects"
                        previewFn={importProspectsPreview}
                        commitFn={importProspectsCommit}
                        sampleExportUrl="/api/exports/prospects"
                        sampleExportLabel="export actuel des prospects"
                        labelColumns="type_contact, statut, societe, prenom, nom, siret, telephone, telephone_2, email, email_2, adresse_facturation, source_lead, nature_besoin, type_travaux, commentaire..."
                    />
                </TabsContent>

                <TabsContent value="projects" className="mt-4">
                    <ImportPanel
                        kind="projects"
                        previewFn={importProjectsPreview}
                        commitFn={importProjectsCommit}
                        sampleExportUrl="/api/exports/projects"
                        sampleExportLabel="export actuel des projets"
                        labelColumns="Onglet &apos;Vue d&apos;ensemble&apos; : Dossier ID, Titre, Statut, Client, N° devis, Montant devis HT, Date signature, Date début chantier, Date fin chantier, Type de travaux..."
                        supportsChildren
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
